import { IELTSSection } from './types';

/**
 * Section 介绍文案接口
 */
export interface SectionIntro {
  sectionId: string;
  title: string;
  description: string;
  tips?: string[];
  duration?: number;
}

/**
 * IELTS 听力理解介绍
 */
const IELTS_LISTENING_INTRO: SectionIntro = {
  sectionId: IELTSSection.LISTENING,
  title: '听力 (Listening)',
  description: 'IELTS 听力部分测试您在各种情境下对英语口语的理解能力。这部分包含4个部分，共40道题目，内容涵盖日常生活、教育、工作等多种场景，包括对话、独白、讨论等形式。',
  duration: 30,
  tips: [
    '在音频播放前，先快速浏览题目，了解需要关注的关键信息',
    '注意听力材料中的同义词替换，答案可能不是原词',
    '听清细节信息，如数字、时间、地点等',
    '注意转折词（but, however, although）后面的内容往往是重点',
    '注意说话者的语气和态度，有助于理解语境',
    '如果错过某部分内容，不要纠结，继续听下面的内容',
    '练习时可以听一遍，但考试时要珍惜每次播放机会',
  ],
};

/**
 * IELTS 阅读理解介绍
 */
const IELTS_READING_INTRO: SectionIntro = {
  sectionId: IELTSSection.READING,
  title: '阅读 (Reading)',
  description: 'IELTS 阅读部分测试您阅读理解能力。学术类包含3篇长文，培训类包含多篇短文，内容涵盖学术、社会、科技、文化等领域，共40道题目。',
  duration: 60,
  tips: [
    '先快速浏览文章标题、段落首句，了解文章结构和主题',
    '仔细阅读题目，明确需要查找的信息类型',
    '使用略读（skimming）技巧快速定位关键信息',
    '使用扫读（scanning）技巧查找具体信息',
    '注意文章中的同义词替换，答案可能与原文用词不同',
    '注意判断题（TRUE/FALSE/NOT GIVEN），分清"未提及"和"错误"',
    '合理分配时间，三篇文章的时间应该大致相等',
  ],
};

/**
 * IELTS 写作介绍
 */
const IELTS_WRITING_INTRO: SectionIntro = {
  sectionId: IELTSSection.WRITING,
  title: '写作 (Writing)',
  description: 'IELTS 写作部分测试您的英语书面表达能力。包括两项任务：Task 1（图表描述或书信，150词以上）和 Task 2（议论文，250词以上）。学术类Task 1为图表描述，培训类Task 1为书信写作。',
  duration: 60,
  tips: [
    'Task 1 耗时约20分钟，Task 2 耗时约40分钟，合理分配时间',
    'Task 2 分数占比更高（2/3），应投入更多精力',
    '写作前先花2-3分钟规划文章结构',
    '确保字数达标：Task 1 至少150词，Task 2 至少250词',
    '使用多样化的句式和词汇，展现语言能力',
    '注意段落结构：开头段、主体段、结尾段',
    '使用恰当的连接词，使文章逻辑清晰',
    'Task 1 要包含所有关键数据，Task 2 要有明确的观点和充分的论证',
    '留2-3分钟检查语法和拼写错误',
  ],
};

/**
 * IELTS 口语介绍
 */
const IELTS_SPEAKING_INTRO: SectionIntro = {
  sectionId: IELTSSection.SPEAKING,
  title: '口语 (Speaking)',
  description: 'IELTS 口语部分是与考官一对一的面试，持续11-14分钟。分为三个部分：Part 1（个人话题，4-5分钟）、Part 2（话题卡片，3-4分钟）、Part 3（深入讨论，4-5分钟）。',
  duration: 11,
  tips: [
    '自信地表达，即使犯错也不要紧张，继续说下去',
    'Part 1 回答要简洁直接，但不要过于简短',
    'Part 2 注意时间控制，充分利用2分钟准备时间',
    'Part 2 要涵盖话题卡上的所有要点',
    'Part 3 回答要深入，展现分析能力，不要只是简单回答"是"或"否"',
    '使用多样的词汇和语法结构',
    '如果听不清问题，可以礼貌地请考官重复',
    '注意发音清晰，语速适中',
    '练习时录音回听，自我改进',
  ],
};

/**
 * IELTS Section 介绍文案映射
 */
export const IELTS_SECTION_INTROS: Record<string, SectionIntro> = {
  [IELTSSection.LISTENING]: IELTS_LISTENING_INTRO,
  [IELTSSection.READING]: IELTS_READING_INTRO,
  [IELTSSection.WRITING]: IELTS_WRITING_INTRO,
  [IELTSSection.SPEAKING]: IELTS_SPEAKING_INTRO,
};

/**
 * 获取 IELTS Section 介绍文案
 */
export function getIELTSSectionIntro(sectionId: string): SectionIntro | null {
  return IELTS_SECTION_INTROS[sectionId] || null;
}
